import { useCallback, useMemo } from 'react';
import { useDataProvider, useSafeSetState } from 'react-admin';
import {
    SearchDataProvider,
    SearchRequestOptions,
    SearchResultDataItem,
    SearchResult,
} from '../types';

import { useQuery, UseQueryOptions } from 'react-query';

/**
 * Get a callback to fetch global search results from the data provider using its `search` method.
 *
 * The request starts when the callback is called.
 *
 * useSearch() parameters can be passed:
 *
 * - at definition time
 *
 *       const [search] = useSearch({ targets: ['artists', 'albums'] });
 *       search(query);
 *
 * - at call time
 *
 *       const [search] = useSearch();
 *       search(query, { targets: ['artists', 'albums'] });
 *
 * - both, in which case the definition and call time parameters are merged
 *
 *       const [search] = useSearch({ targets: ['artists', 'albums'] });
 *       search(query, { targets: ['concerts'] });
 *
 * @param {Object} options
 *   - {String[]} targets: Optional. The indices for which the search should be performed.
 *
 * @returns A tuple with the search callback and the request state. Destructure as [search, { query, data, total, error, isLoading, isLoaded }].
 *
 * The return value updates according to the request state:
 *
 * - mount:         [search, { isLoading: false, isLoaded: false }]
 * - search called: [search, { isLoading: true, isLoaded: false }]
 * - success:       [search, { query: [string], ata: [data from response], total: [total from response], isLoading: false, isLoaded: true }]
 * - error:         [search, { query: [string], error: [error from response], isLoading: false, isLoaded: true }]
 *
 * The search function accepts the following arguments:
 * - {String} query: The terms to search for
 * - {Object} options
 *   - {String[]} targets: Optional. The indices for which the search should be performed.
 */
export const useSearch = (
    defaultOptions?: SearchRequestOptions,
    queryOptions?: Omit<UseQueryOptions<SearchResult>, 'enabled'>
): UseSearchValue => {
    const [runQuery, setRunQuery] = useSafeSetState(false);
    const [searchParams, setSearchParams] = useSafeSetState<{
        query: string;
        options?: SearchRequestOptions;
    }>({
        query: '',
        options: {},
    });
    const dataProvider = useDataProvider() as SearchDataProvider;
    const { query, options } = searchParams;
    const searchOptions = {
        ...defaultOptions,
        ...options,
    };

    const {
        data,
        isLoading,
        isFetched: isLoaded,
        error,
    } = useQuery(
        ['search', query, searchOptions],
        () => dataProvider.search(query, searchOptions),
        {
            keepPreviousData: true,
            ...(queryOptions || {}),
            enabled: runQuery,
            onSettled: (data, error) => {
                setRunQuery(false);
                queryOptions?.onSettled && queryOptions.onSettled(data, error);
            },
        }
    );

    const search: UseSearchCallback = useCallback(
        (query: string, options: SearchRequestOptions = {}) => {
            setSearchParams(() => ({
                query,
                options,
            }));
            setRunQuery(query.length > 0);
        },
        [setSearchParams, setRunQuery]
    );

    const state = useMemo(
        () => ({
            data: data?.data,
            total: data?.total,
            error,
            isLoading,
            isLoaded,
            query,
        }),
        [data?.data, data?.total, error, isLoaded, isLoading, query]
    );

    return [search, state];
};

export interface QueryOptions {
    action?: string;
    onSuccess?: (response: any) => any;
    onFailure?: (error?: any) => any;
    withDeclarativeSideEffectsSupport?: boolean;
}

export type UseSearchData = {
    data?: SearchResultDataItem[];
    total?: number;
    error?: any;
    isLoading: boolean;
    isLoaded: boolean;
    query: string;
};

export type UseSearchCallback = (
    query: string,
    options?: SearchRequestOptions
) => void;

export type UseSearchValue = [UseSearchCallback, UseSearchData];
